import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { MDBTable, MDBTableHead, MDBTableBody } from 'mdb-react-ui-kit';
import { getInfluencersList, addCampaignInfluencer } from '../services/api';
import InstagramSvg from '../assets/icons/instagram.svg';
import TikTokSvg from '../assets/icons/tiktok-icon.svg';
import ContentForm from './ContentForm';

const InfluencerSelection = ({ isOpen, handleClose, campaignID }) => { 
    const [influencers, setInfluencers] = useState([]);
    const [selectedInfluencers, setSelectedInfluencers] = useState([]);
    const [showContentFormSelection, setShowContentFormSelection] = useState(false);

    useEffect(() => {
        const enrichInfluencerData = (influencer) => {
            const niches = influencer.niche.split(',').map(niche => niche.trim());
            const platform = influencer.instagramUsername && influencer.tikTokUsername
                ? 'Instagram & TikTok'
                : influencer.instagramUsername ? 'Instagram' : 'TikTok';

            const enrichedInfluencer = { ...influencer, niches, platform };

            if (influencer.workPlatformName === 'Instagram') {
                enrichedInfluencer.IG_followers = influencer.followers;
                enrichedInfluencer.IG_engagementRate = influencer.engagementRate * 100;
                enrichedInfluencer.IG_avgLikes = influencer.averageLikes;
                enrichedInfluencer.IG_avgComments = influencer.averageComments;
            } else if (influencer.workPlatformName === 'TikTok') {
                enrichedInfluencer.TT_followers = influencer.followers;
                enrichedInfluencer.TT_engagementRate = influencer.engagementRate * 100;
                enrichedInfluencer.TT_avgLikes = influencer.averageLikes;
                enrichedInfluencer.TT_avgComments = influencer.averageComments;
            }

            return enrichedInfluencer;
        };

        const fetchData = async () => {
            try {
                const { data } = await getInfluencersList();
                const enrichedData = data.map(enrichInfluencerData);
                setInfluencers(enrichedData);
            } catch (error) {
                console.error('Error fetching influencers data:', error);
            }
        };

        fetchData();
    }, []);

    const handleSelectInfluencer = (influencerID) => {
        setSelectedInfluencers(prevState =>
            prevState.includes(influencerID)
                ? prevState.filter(id => id !== influencerID)
                : [...prevState, influencerID]
        );
    };

    const handleSubmit = async () => {
        try {
            for (const influencerID of selectedInfluencers) {
                await addCampaignInfluencer({ campaignID, influencerID });
            }
            setShowContentFormSelection(true);
            //handleClose();
        } catch (error) {
            console.error("Error adding campaign influencer:", error);
        }
    };

    const formatFollowers = (count) => {
        const formats = [
            { threshold: 1e9, suffix: 'B' },
            { threshold: 1e6, suffix: 'M' },
            { threshold: 1e3, suffix: 'K' }
        ];

        const format = formats.find(f => count >= f.threshold);
        if (!format) return count.toString();

        return (count / format.threshold).toFixed(1).replace(/\.0$/, '') + format.suffix;
    };

    const handleContentFormClose = () => {
        setShowContentFormSelection(false);
        handleClose();
    };

    return (
        <>
        <Modal show={isOpen} onHide={handleClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Select Influencers</Modal.Title>
            </Modal.Header>
            <Modal.Body className='modal-body'>
                {influencers.length > 0 ? (
                    <MDBTable align='left' striped hover responsive>
                        <MDBTableHead>
                            <tr style={{ backgroundColor: '#f0f0f0', color: '#7d7d7d' }}>
                                <th>#</th>
                                <th>Select</th>
                                <th>Influencer</th>
                                <th>Platform</th>
                                <th>Followers</th>
                            </tr>
                        </MDBTableHead>
                        <MDBTableBody>
                            {influencers.map((influencer, index) => (
                                <tr key={influencer.influencerId}>
                                    <td>{index + 1}</td>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedInfluencers.includes(influencer.influencerId)}
                                            onChange={() => handleSelectInfluencer(influencer.influencerId)}
                                        />
                                    </td>
                                    <td className='d-flex align-items-center'>
                                        <img
                                            src={influencer.profileImg || 'https://mdbcdn.b-cdn.net/img/Photos/new-templates/bootstrap-profiles/avatar-2.webp'}
                                            alt='profile'
                                            style={{ width: '40px', height: '40px' }}
                                            className='rounded-circle me-2'
                                        />
                                        <div>
                                            <p className='fw-bold mb-0'>{influencer.creatorName}</p>
                                            <small className='text-muted'>{influencer.email}</small>
                                        </div>
                                    </td>
                                    <td>
                                        <>
                                            {influencer.platform === 'Instagram' && (
                                                <a
                                                    href={`https://instagram.com/${influencer.instagramUsername.replace('@', '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <img
                                                        src={InstagramSvg}
                                                        alt="Instagram"
                                                        style={{ width: '20px', height: '20px', marginRight: '5px' }}
                                                    />
                                                    {influencer.instagramUsername}
                                                </a>
                                            )}
                                            {influencer.platform === 'TikTok' && (
                                                <a
                                                    href={`https://tiktok.com/${influencer.tikTokUsername.startsWith('@') ? influencer.tikTokUsername : `@${influencer.tikTokUsername}`}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <img
                                                        src={TikTokSvg}
                                                        alt="TikTok"
                                                        style={{ width: '20px', height: '20px', marginRight: '5px' }}
                                                    />
                                                    {influencer.tikTokUsername}
                                                </a>
                                            )}
                                            {influencer.platform === 'Instagram & TikTok' && (
                                                <>
                                                    <a
                                                        href={`https://instagram.com/${influencer.instagramUsername.replace('@', '')}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <img
                                                            src={InstagramSvg}
                                                            className="mx-1 my-1"
                                                            alt="Instagram"
                                                            style={{ width: '20px', height: '20px' }}
                                                        />
                                                        {influencer.instagramUsername}
                                                    </a>
                                                    <br />
                                                    <a
                                                        href={`https://tiktok.com/${influencer.tikTokUsername.startsWith('@') ? influencer.tikTokUsername : `@${influencer.tikTokUsername}`}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <img
                                                            src={TikTokSvg}
                                                            alt="TikTok"
                                                            className="mx-1 my-1"
                                                            style={{ width: '20px', height: '20px' }}
                                                        />
                                                        {influencer.tikTokUsername}
                                                    </a>
                                                </>
                                            )}
                                        </>
                                    </td>
                                    <td>
                                        <div>
                                            <img src={InstagramSvg} alt="Instagram" className='my-1' style={{ width: '20px', height: '20px', marginRight: '2px' }} />
                                            {formatFollowers(influencer?.IG_followers || 0)}
                                        </div>
                                        <div>
                                            <img src={TikTokSvg} alt="TikTok" className='my-1' style={{ width: '20px', height: '20px', marginRight: '2px' }} />
                                            {formatFollowers(influencer?.TT_followers || 0)}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </MDBTableBody>
                    </MDBTable>
                ) : (
                    <p>No influencers available.</p>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>Close</Button>
                <Button variant="primary" onClick={handleSubmit}>Save</Button>
            </Modal.Footer>
        </Modal>
       
            <ContentForm
                isOpen={showContentFormSelection}
                handleClose={handleContentFormClose}
                selectedInfluencers={selectedInfluencers}
                campaignID={campaignID}
            />
        </>
    );
};

export default InfluencerSelection;